import React from 'react';
import type { AiJob } from '../../../lib/supabase.types';
import { formatClaimElapsed, isAiJobInProgress } from '../../../lib/aiJobLabels';
import { operatorStageForStatus, type OperatorStage } from '../../../lib/aiJobSummary';
import { Icon } from '../../ui';

/**
 * Spec → Build → Approve → Live on one line: a circle and a label per stage,
 * a rail between them. Done nodes fill with accent and a check; the current
 * node breathes; future nodes are hairline. No captions (they cost height);
 * the current node's state is in its tooltip instead.
 */

const STAGES: { id: OperatorStage; label: string }[] = [
  { id: 'spec', label: 'Spec' },
  { id: 'build', label: 'Build' },
  { id: 'approve', label: 'Approve' },
  { id: 'live', label: 'Live' },
];

type NodeState = 'done' | 'current' | 'future';

/** One line of state for the tooltip on the current node. */
function currentCaption(job: AiJob): string {
  const s = job.status;
  if (isAiJobInProgress(s)) {
    const who = job.claimed_by || 'agent';
    const t = formatClaimElapsed(job.claimed_at);
    return t ? `${who} · ${t}` : who;
  }
  const rebuild = s.startsWith('build') && !!job.approval_body;
  if (s === 'spec_ready' || s === 'build_ready' || s === 'unwind_ready' || s === 'complete') return rebuild ? 'rebuild · waiting on you' : 'waiting on you';
  if (s === 'spec_queued' || s === 'build_queued' || s === 'unwind_queued' || s === 'approved') return 'in queue';
  if (s === 'spec_revision' || s === 'build_revision') return rebuild ? 'rebuild · reply queued' : 'your reply is queued';
  if (s === 'build_staged') return rebuild ? 'rebuild · lock held' : 'staged · lock held';
  if (s === 'submitted') return 'submitted';
  if (s === 'cancelled') return 'cancelled';
  return '';
}

export default function StageStrip({ job, style }: { job: AiJob; style?: React.CSSProperties }) {
  const current = operatorStageForStatus(job.status);
  const currentIndex = STAGES.findIndex((s) => s.id === current);
  const isCancelled = job.status === 'cancelled';
  const isUnwind = job.status.startsWith('unwind');
  const submitted = job.status === 'submitted';

  return (
    <div style={{ display: 'flex', alignItems: 'center', ...style }} aria-label="Job stages">
      {STAGES.map((stage, i) => {
        let state: NodeState = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'future';
        if (submitted) state = 'done';
        const railDone = i < currentIndex || submitted;
        const label = isUnwind && stage.id === 'build' ? 'Unwind' : stage.label;
        const nodeColor = isCancelled && state === 'current' ? 'var(--text-faint)' : isUnwind && state === 'current' ? 'var(--status-down)' : 'var(--accent)';
        const tip = state === 'current' ? currentCaption(job) : state === 'done' ? 'done' : 'not yet';

        return (
          <React.Fragment key={stage.id}>
            {i > 0 ? (
              <div
                aria-hidden
                // Shorter rails on phones so all four stages fit 375px.
                className="mx-[4px] w-[12px] min-[400px]:mx-[6px] min-[400px]:w-[18px] md:mx-[8px] md:w-[28px]"
                style={{
                  height: '2px',
                  borderRadius: 'var(--radius-pill)',
                  background: railDone ? 'var(--accent)' : 'var(--border-hairline)',
                  opacity: railDone ? 0.85 : 1,
                  flexShrink: 0,
                  transition: 'background var(--dur-slow) var(--ease-out)',
                }}
              />
            ) : null}
            <div className="gap-[5px] md:gap-[7px]" style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }} title={`${label} · ${tip}`}>
              <span
                className={state === 'current' && !isCancelled ? 'ck-op-node--current' : undefined}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: 'var(--radius-pill)',
                  background: state === 'done' ? 'var(--accent)' : state === 'current' ? 'var(--surface-card)' : 'transparent',
                  border:
                    state === 'done'
                      ? '2px solid var(--accent)'
                      : state === 'current'
                        ? `2px solid ${nodeColor}`
                        : '1.5px solid var(--border-strong)',
                  color: state === 'done' ? 'var(--text-on-accent)' : nodeColor,
                  boxSizing: 'border-box',
                  transition: 'background var(--dur-slow) var(--ease-out), border-color var(--dur-slow) var(--ease-out)',
                }}
              >
                {state === 'done' ? (
                  <Icon name="check" size={11} strokeWidth={2.75} />
                ) : state === 'current' ? (
                  // 8px, not 7: the ring's field is 16px (20 less two 2px borders),
                  // so an odd dot lands on a half pixel and the gap above it
                  // antialiases differently from the gap below.
                  <span style={{ width: '8px', height: '8px', borderRadius: 'var(--radius-pill)', background: nodeColor }} />
                ) : null}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: (state === 'current' ? 'var(--weight-semibold)' : 'var(--weight-medium)') as React.CSSProperties['fontWeight'],
                  color: state === 'future' ? 'var(--text-faint)' : 'var(--text-body)',
                  letterSpacing: 'var(--tracking-tight)',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
